import { LegalPage, LegalSection } from "@/components/Legal";
import { BETA_EMAIL } from "@/components/Layout";
import { Brand } from "@/components/Logo";

const EFFECTIVE = "June 2026";

export default function Terms() {
  return (
    <LegalPage
      testId="terms-page"
      title="Terms of Service"
      subtitle="A lightweight, plain-language agreement for a product that's still in beta."
      effectiveDate={EFFECTIVE}
    >
      <LegalSection title="1. Acceptance of terms">
        <p>
          By accessing or using <Brand /> (the “Service”), you agree to these Terms of
          Service. If you don't agree, please don't use the Service.
        </p>
      </LegalSection>

      <LegalSection title="2. Description of the service">
        <p>
          <Brand /> is a personal relationship management application designed to help
          you remember context, stay in touch, and follow up with the people in your
          network. The Service is under active development, and features are offered on a
          beta basis.
        </p>
      </LegalSection>

      <LegalSection title="3. Beta and no-warranty disclaimer" highlight>
        <p>
          The Service is currently in beta and is provided “as is” and “as available,”
          without warranties of any kind, whether express or implied. Features may change,
          break, or be discontinued, and the Service may contain bugs or experience
          interruptions. Beta access may be limited, modified, or revoked at our discretion.
        </p>
      </LegalSection>

      <LegalSection title="4. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Use the Service for any unlawful purpose or to upload unlawful content.</li>
          <li>Misuse, disrupt, or attempt to gain unauthorized access to the Service.</li>
          <li>Upload data you don't have the right to store or share.</li>
          <li>Interfere with other users' use of the Service.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Accounts and security">
        <p>
          You're responsible for maintaining the confidentiality of your account credentials
          and for activity that occurs under your account. Please notify us promptly of any
          unauthorized use or suspected security issue at{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="6. Intellectual property">
        <p>
          The Service, including its software, design, and branding, is owned by
          <Brand /> and protected by applicable laws. You retain ownership of the
          content and data you add to the Service; you grant us the limited rights needed to
          operate and provide the Service to you.
        </p>
      </LegalSection>

      <LegalSection title="7. Termination">
        <p>
          You may stop using the Service at any time and request deletion of your account. We
          may suspend or terminate access to protect the Service or other users, to comply
          with the law, or if these Terms are violated.
        </p>
      </LegalSection>

      <LegalSection title="8. Limitation of liability">
        <p>
          To the maximum extent permitted by law, <Brand /> will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or for any loss
          of data, arising from your use of the Service. The Service is provided on a beta
          basis and you use it at your own discretion.
        </p>
      </LegalSection>

      <LegalSection title="9. Changes to the service">
        <p>
          Because we're actively building, we may add, change, or discontinue features at any
          time. We may also update these Terms; when we make material changes, we'll update
          the effective date above. Continued use after changes means you accept the updated
          Terms.
        </p>
      </LegalSection>

      <LegalSection title="10. Governing law">
        <p>
          These Terms are governed by the laws of the Province of Alberta, Canada, without
          regard to its conflict-of-laws principles. You agree to the exclusive jurisdiction
          of the courts located in Alberta, Canada for any disputes arising from these Terms
          or the Service.
        </p>
      </LegalSection>

      <LegalSection title="11. Contact information">
        <p>
          Questions about these Terms? Email{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
